import type { AxiosProgressEvent } from "axios";
import { http } from "@/lib/http";
import { rewriteMediaUrl } from "@/lib/media-url";
import { normalizeUser } from "@/lib/normalizers";
import type { Comment, CourseFolderDetail, CourseLesson, CourseLessonFollowUp, CoursePurchaseState, FanFolder, Membership, PaginatedResult, Post, Tag } from "@/types/content";
import type { PostTypeSlug } from "@/constants/posts";

const DEFAULT_MEDIA_CHUNK_SIZE_BYTES = 8 * 1024 * 1024;
const MAX_CHUNK_RETRIES = 3;

export interface HomeBootstrap {
  featuredPosts: Post[];
  folders: FanFolder[];
  tags: Tag[];
  memberships: Membership[];
  posts: PaginatedResult<Post>;
}

export interface PostFilters {
  page?: number;
  tags?: string[];
  folderIds?: number[];
  tierIds?: number[];
  type?: string;
  folderId?: number;
  query?: string;
  userTierId?: string;
  sort?: "newest" | "liked" | "viewed" | "commented";
}

export interface SavePostInput {
  id?: number;
  title: string;
  subtitle?: string;
  description?: string;
  tags: string[];
  folderId?: number | null;
  membershipId?: number | null;
  publish: boolean;
  mainFeedExcluded: boolean;
  isCountdown: boolean;
  scheduledAt?: string;
  type: PostTypeSlug;
  coursePartNumber?: number | null;
  courseIsIntro?: boolean | null;
  lessonFollowUp?: CourseLessonFollowUp | null;
  externalUrl?: string;
  file?: File | null;
  files?: File[];
  onUploadProgress?: (progress: number) => void;
}

export interface CoursePurchaseAttempt {
  status: string;
  message?: string | null;
  payment?: {
    id: string;
    clientSecret?: string | null;
    status?: string | null;
    paymentMethod?: string | null;
    amount?: number | null;
    currency?: string | null;
  } | null;
  purchase: CoursePurchaseState;
}

function normalizeCoursePurchaseState(value: unknown): CoursePurchaseState | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const source = value as Record<string, unknown>;

  return {
    enabled: Boolean(source.enabled),
    priceAmount: Number(source.price_amount ?? 0),
    price: source.price as string | null | undefined,
    currency: source.currency as string | null | undefined,
    hasAccess: Boolean(source.has_access),
    purchaseId: source.purchase_id == null ? null : Number(source.purchase_id),
    stripePaymentIntentId: source.stripe_payment_intent_id as string | null | undefined,
    purchasedAt: source.purchased_at as string | null | undefined,
  };
}

function normalizeLessonFollowUp(value: unknown): CourseLessonFollowUp | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const source = value as Record<string, unknown>;
  const questions = Array.isArray(source.questions)
    ? source.questions
        .map((question) => {
          if (!question || typeof question !== "object") {
            return null;
          }

          const record = question as Record<string, unknown>;
          const choices = Array.isArray(record.choices)
            ? record.choices.map((choice) => String(choice ?? "").trim()).filter(Boolean)
            : [];
          const prompt = String(record.prompt ?? "").trim();

          if (!prompt) {
            return null;
          }

          return {
            prompt,
            helperText: String(record.helper_text ?? record.helperText ?? "").trim() || null,
            choices,
            correctChoiceIndex: record.correct_choice_index == null ? null : Number(record.correct_choice_index),
          };
        })
        .filter((question): question is NonNullable<typeof question> => Boolean(question))
    : [];

  const reminder = String(source.reminder ?? "").trim() || null;
  const notes = String(source.notes ?? "").trim() || null;

  if (!reminder && !notes && questions.length === 0) {
    return null;
  }

  return {
    reminder,
    notes,
    questions,
  };
}

function toRecordArray(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value as Record<string, unknown>[];
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).filter(
      (entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object",
    );
  }

  return [];
}

function parseMeta(meta: Record<string, unknown> | undefined) {
  return {
    total: Number(meta?.total ?? 0),
    currentPage: Number(meta?.current_page ?? 1),
    lastPage: Number(meta?.last_page ?? 1),
  };
}

function inferMediaType(item: Record<string, unknown>) {
  if (typeof item.type === "string" && item.type.trim()) {
    return item.type;
  }

  const candidate = String(item.url ?? item.thumbnail_url ?? "");
  const path = candidate.split("?")[0]?.toLowerCase() ?? "";

  if (/\.(mp4|mov|mpeg|webm)$/.test(path)) {
    return "video";
  }

  if (/\.(mp3|wav|m4a|aac|ogg)$/.test(path)) {
    return "audio";
  }

  if (/\.(jpg|jpeg|png|gif|webp|avif)$/.test(path)) {
    return "image";
  }

  return null;
}

function resolveThumbnailValue(rawThumbnail: unknown, rawMediaUrl: unknown): string {
  const thumbnail = String(rawThumbnail ?? "").trim();
  if (!thumbnail) {
    return "";
  }

  // Full or path-based values can be handled by rewriteMediaUrl directly.
  if (
    thumbnail.includes("/")
    || thumbnail.startsWith("http://")
    || thumbnail.startsWith("https://")
    || thumbnail.startsWith("data:")
  ) {
    return thumbnail;
  }

  // Some legacy records only store a basename like "abc_thumbnail.png".
  // Resolve it relative to the media file directory.
  const mediaUrl = String(rawMediaUrl ?? "").trim();
  if (!mediaUrl) {
    return thumbnail;
  }

  const withoutQuery = mediaUrl.split("?")[0] ?? mediaUrl;
  const lastSlash = withoutQuery.lastIndexOf("/");
  if (lastSlash < 0) {
    return thumbnail;
  }

  return `${withoutQuery.slice(0, lastSlash + 1)}${thumbnail}`;
}

function toBooleanFlag(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
  }

  return false;
}

function resolveIntroFlag(item: Record<string, unknown>): boolean {
  return toBooleanFlag(item.lesson_is_intro ?? item.is_intro ?? item.isIntro);
}

export function normalizeComment(comment: Record<string, unknown>): Comment {
  const replies = Array.isArray(comment.replies) ? comment.replies.map((reply) => normalizeComment(reply as Record<string, unknown>)) : [];
  return {
    id: Number(comment.id),
    parentCommentId: comment.parent_comment_id as number | null | undefined,
    user: normalizeUser((comment.user as Record<string, unknown> | null | undefined) ?? null),
    myLikeScore: comment.my_like_score as number | null | undefined,
    likesCount: comment.likes_count as number | null | undefined,
    dislikesCount: comment.dislikes_count as number | null | undefined,
    repliesCount: comment.replies_count as number | null | undefined,
    commentedUser: normalizeUser((comment.commented_user as Record<string, unknown> | null | undefined) ?? null),
    comment: String(comment.comment ?? ""),
    replies,
    createdAgo: comment.created_ago as string | null | undefined,
  };
}

export function normalizePost(post: Record<string, unknown>): Post {
  return {
    id: Number(post.id),
    title: String(post.title ?? ""),
    subtitle: String(post.subtitle ?? ""),
    slug: post.slug as string | null | undefined,
    text: post.text as string | null | undefined,
    user: normalizeUser((post.user as Record<string, unknown> | null | undefined) ?? null),
    media: Array.isArray(post.media)
      ? post.media.map((media) => ({
          id: Number((media as Record<string, unknown>).id),
          postId: Number((media as Record<string, unknown>).post_id),
          url: rewriteMediaUrl(String((media as Record<string, unknown>).url ?? "")) ?? "",
          type: inferMediaType(media as Record<string, unknown>),
          externalUrl: (media as Record<string, unknown>).external_url as string | null | undefined,
          thumbnailUrl:
            rewriteMediaUrl(
              resolveThumbnailValue(
                (media as Record<string, unknown>).thumbnail_url,
                (media as Record<string, unknown>).url,
              ),
            ) ?? "",
        }))
      : [],
    tags: Array.isArray(post.tags)
      ? post.tags.map((tag) => ({
          id: Number((tag as Record<string, unknown>).id),
          title: String((tag as Record<string, unknown>).title ?? ""),
          url: (tag as Record<string, unknown>).url as string | null | undefined,
          slug: (tag as Record<string, unknown>).slug as string | null | undefined,
        }))
      : [],
    commentsCount: post.comments_count as number | null | undefined,
    comments: Array.isArray(post.comments) ? post.comments.map((comment) => normalizeComment(comment as Record<string, unknown>)) : [],
    myLikeScore: post.my_like_score as number | null | undefined,
    likesCount: post.likes_count as number | null | undefined,
    viewsCount: post.views_count as number | null | undefined,
    dislikesCount: post.dislikes_count as number | null | undefined,
    isBookmarked: post.is_bookmarked as number | null | undefined,
    folderId: post.folder_id as number | null | undefined,
    membershipId: post.membership_id as number | null | undefined,
    coursePartNumber: post.course_part_number as number | null | undefined,
    isIntro: resolveIntroFlag(post),
    course: post.course && typeof post.course === "object"
      ? {
          id: Number((post.course as Record<string, unknown>).id ?? 0),
          name: String((post.course as Record<string, unknown>).name ?? ""),
          slug: (post.course as Record<string, unknown>).slug as string | null | undefined,
          isLocked: Boolean((post.course as Record<string, unknown>).is_locked),
          oneTimePurchase: normalizeCoursePurchaseState((post.course as Record<string, unknown>).one_time_purchase),
        }
      : null,
    lessonFollowUp: normalizeLessonFollowUp(post.lesson_follow_up),
    isLocked: post.is_locked as boolean | null | undefined,
    isPublished: post.is_published as number | null | undefined,
    isFeatured: post.is_featured as number | null | undefined,
    mainFeedExcluded: post.is_main_feed as number | null | undefined,
    isCountdown: post.is_countdown as number | null | undefined,
    createdAgo: post.created_ago as string | null | undefined,
    type: post.type as string | null | undefined,
    externalUrl: post.external_url as string | null | undefined,
    scheduledAt: post.scheduled_at as string | null | undefined,
    externalLink: post.external_link as Post["externalLink"],
  };
}

export async function fetchPosts(filters: PostFilters = {}): Promise<PaginatedResult<Post>> {
  const params = new URLSearchParams();
  params.set("page", String(filters.page ?? 1));
  for (const id of filters.tierIds ?? []) params.append("tiers[]", String(id));
  for (const id of filters.folderIds ?? []) params.append("folders[]", String(id));
  for (const tag of filters.tags ?? []) params.append("tags[]", tag);
  if (filters.folderId && filters.folderId !== -1) params.append("folders[]", String(filters.folderId));
  if (filters.type && filters.type !== "all") params.set("type", filters.type);
  if (filters.query) params.set("phrase", filters.query);
  if (filters.userTierId) params.set("userTierId", filters.userTierId);
  if (filters.sort) params.set("sort", filters.sort);

  const response = await http.get(`/api/v1/posts?${params.toString()}`);
  return {
    data: Array.isArray(response.data?.data) ? response.data.data.map((post: Record<string, unknown>) => normalizePost(post)) : [],
    meta: parseMeta(response.data?.meta),
  };
}

export async function fetchPost(postId: string | number): Promise<Post> {
  const response = await http.get(`/api/v1/posts/${postId}`);
  return normalizePost(response.data?.data ?? {});
}

export async function fetchCourseFolder(folderId: number): Promise<CourseFolderDetail> {
  const response = await http.get(`/api/v1/courses/${folderId}`);
  const data = (response.data?.data ?? {}) as Record<string, unknown>;

  return {
    course: {
      id: Number((data.course as Record<string, unknown> | undefined)?.id ?? 0),
      name: String((data.course as Record<string, unknown> | undefined)?.name ?? ""),
      slug: (data.course as Record<string, unknown> | undefined)?.slug as string | null | undefined,
      thumbnailUrl: rewriteMediaUrl((data.course as Record<string, unknown> | undefined)?.thumbnail_url as string | null | undefined),
      titleFolders: (data.course as Record<string, unknown> | undefined)?.title_folders as string | null | undefined,
      isLocked: Boolean((data.course as Record<string, unknown> | undefined)?.is_locked),
      oneTimePurchase: normalizeCoursePurchaseState((data.course as Record<string, unknown> | undefined)?.one_time_purchase),
    },
    topics: Array.isArray(data.topics)
      ? data.topics.map((topic) => ({
          id: Number((topic as Record<string, unknown>).id),
          name: String((topic as Record<string, unknown>).name ?? ""),
          sortId: (topic as Record<string, unknown>).sort_id == null ? null : Number((topic as Record<string, unknown>).sort_id),
          lessons: Array.isArray((topic as Record<string, unknown>).lessons)
            ? ((topic as Record<string, unknown>).lessons as Record<string, unknown>[]).map((lesson) => ({
                ...(normalizePost(lesson) as CourseLesson),
                coursePartNumber: lesson.course_part_number == null ? null : Number(lesson.course_part_number),
                isIntro: resolveIntroFlag(lesson),
                lessonFollowUp: normalizeLessonFollowUp(lesson.lesson_follow_up),
                progress: {
                  isCompleted: Boolean(lesson.is_completed),
                  score: Number(lesson.score ?? 0),
                  watchSeconds: Number(lesson.watch_seconds ?? 0),
                  videoSeconds: Number(lesson.video_seconds ?? 0),
                },
              }))
            : [],
        }))
      : [],
    summary: {
      completedLessons: Number((data.summary as Record<string, unknown> | undefined)?.completed_lessons ?? 0),
      totalLessons: Number((data.summary as Record<string, unknown> | undefined)?.total_lessons ?? 0),
      score: Number((data.summary as Record<string, unknown> | undefined)?.score ?? 0),
    },
  };
}

export async function markCourseLessonComplete(postId: number, watchSeconds: number, videoSeconds: number) {
  const response = await http.post("/api/v1/courses/progress", {
    post_id: postId,
    watch_seconds: Math.max(0, Math.round(watchSeconds)),
    video_seconds: Math.max(0, Math.round(videoSeconds)),
    completed: true,
  });

  return response.data?.data as Record<string, unknown>;
}

function normalizeCoursePurchaseAttempt(data: Record<string, unknown>): CoursePurchaseAttempt {
  return {
    status: String(data.status ?? ""),
    message: data.message as string | null | undefined,
    payment: data.payment && typeof data.payment === "object"
      ? {
          id: String((data.payment as Record<string, unknown>).id ?? ""),
          clientSecret: (data.payment as Record<string, unknown>).client_secret as string | null | undefined,
          status: (data.payment as Record<string, unknown>).status as string | null | undefined,
          paymentMethod: (data.payment as Record<string, unknown>).payment_method as string | null | undefined,
          amount: (data.payment as Record<string, unknown>).amount == null ? null : Number((data.payment as Record<string, unknown>).amount),
          currency: (data.payment as Record<string, unknown>).currency as string | null | undefined,
        }
      : null,
    purchase: normalizeCoursePurchaseState(data.purchase) ?? {
      enabled: false,
      priceAmount: 0,
      price: null,
      currency: null,
      hasAccess: false,
      purchaseId: null,
      stripePaymentIntentId: null,
      purchasedAt: null,
    },
  };
}

export async function purchaseCourseAccess(folderId: number, paymentMethodId: string, discountCode?: string): Promise<CoursePurchaseAttempt> {
  const response = await http.post(`/api/v1/courses/${folderId}/purchase`, {
    payment_method_nonce: paymentMethodId,
    discount_code: discountCode?.trim() || undefined,
  });

  return normalizeCoursePurchaseAttempt((response.data?.data ?? {}) as Record<string, unknown>);
}

export async function confirmCourseAccessPurchase(folderId: number, paymentIntentId: string): Promise<CoursePurchaseAttempt> {
  const response = await http.post(`/api/v1/courses/${folderId}/confirm`, {
    payment_intent_id: paymentIntentId,
  });

  return normalizeCoursePurchaseAttempt((response.data?.data ?? {}) as Record<string, unknown>);
}

export async function reorderCourseLessons(folderId: number, lessonIds: number[]) {
  await http.post(`/api/v1/admin/courses/${folderId}/reorder-parts`, {
    lesson_ids: lessonIds,
  });
}

export async function fetchFeaturedPosts(): Promise<Post[]> {
  const response = await http.get("/api/v1/featured-posts");
  return Array.isArray(response.data?.data?.posts) ? response.data.data.posts.map((post: Record<string, unknown>) => normalizePost(post)) : [];
}

export async function setFeaturedPost(postId: number, startsAt = "1970-01-01", endsAt = "2099-12-31") {
  await http.post("/api/v1/admin/featured-posts/set", {
    post_id: postId,
    starts_at: startsAt,
    ends_at: endsAt,
  });
}

export async function unsetFeaturedPost(postId: number) {
  await http.delete(`/api/v1/admin/featured-posts/${postId}`);
}

export async function fetchHomeBootstrap(): Promise<HomeBootstrap> {
  const response = await http.get("/api/v1/home/bootstrap");
  const data = (response.data?.data ?? {}) as Record<string, unknown>;
  const normalizeFolder = (folder: Record<string, unknown>): FanFolder => ({
    id: Number(folder.id),
    name: String(folder.name ?? ""),
    slug: folder.slug as string | null | undefined,
    type: folder.type as string | null | undefined,
    externalUrl: folder.external_url as string | null | undefined,
    postsCount: folder.posts_count == null ? null : Number(folder.posts_count),
    thumbnailUrl: rewriteMediaUrl(folder.thumbnail_url as string | null | undefined),
    gridDisplay: folder.is_grid_view == null ? null : Number(folder.is_grid_view),
    courseProgress: folder.course_progress && typeof folder.course_progress === "object"
      ? {
          completedLessons: Number((folder.course_progress as Record<string, unknown>).completed_lessons ?? 0),
          totalLessons: Number((folder.course_progress as Record<string, unknown>).total_lessons ?? 0),
          score: Number((folder.course_progress as Record<string, unknown>).score ?? 0),
        }
      : null,
    isLocked: folder.is_locked as boolean | null | undefined,
    oneTimePurchase: normalizeCoursePurchaseState(folder.one_time_purchase),
    subFolders: toRecordArray(folder.subfolders).map((sub) => normalizeFolder(sub)),
  });

  return {
    featuredPosts: Array.isArray(data.featured_posts) ? data.featured_posts.map((post) => normalizePost(post as Record<string, unknown>)) : [],
    folders: toRecordArray(data.folders).map((folder) => normalizeFolder(folder)),
    tags: Array.isArray(data.tags)
      ? data.tags.map((tag: Record<string, unknown>) => ({
          id: Number(tag.id),
          title: String(tag.title ?? ""),
          url: tag.url as string | null | undefined,
          slug: tag.slug as string | null | undefined,
        }))
      : [],
    memberships: Array.isArray(data.memberships)
      ? data.memberships.map((item: Record<string, unknown>) => ({
          id: Number(item.id),
          name: String(item.name ?? ""),
          cashierProductId: item.cashier_product_id as string | null | undefined,
          level: item.level == null ? null : Number(item.level),
          price: item.price as string | null | undefined,
          currency: item.currency as string | null | undefined,
          badgeUrl: rewriteMediaUrl(item.badge_url as string | null | undefined),
          badgeColor: item.badge_color as string | null | undefined,
          isActive: item.is_active == null ? null : Number(item.is_active),
          isSelected: item.is_selected == null ? null : Number(item.is_selected),
          isSubscribed: item.is_subscribed as boolean | null | undefined,
        }))
      : [],
    posts: {
      data: Array.isArray(data.posts) ? data.posts.map((post: Record<string, unknown>) => normalizePost(post)) : [],
      meta: parseMeta((data.posts_meta as Record<string, unknown> | undefined) ?? undefined),
    },
  };
}

export async function fetchBookmarks(page = 1): Promise<PaginatedResult<Post>> {
  const response = await http.get(`/api/v1/bookmarks?page=${page}`);
  return {
    data: Array.isArray(response.data?.data) ? response.data.data.map((post: Record<string, unknown>) => normalizePost(post)) : [],
    meta: parseMeta(response.data?.meta),
  };
}

export async function fetchTags(): Promise<Tag[]> {
  const response = await http.get("/api/v1/tags");
  return Array.isArray(response.data?.data)
    ? response.data.data.map((tag: Record<string, unknown>) => ({
        id: Number(tag.id),
        title: String(tag.title ?? ""),
        url: tag.url as string | null | undefined,
        slug: tag.slug as string | null | undefined,
      }))
    : [];
}

export async function fetchFanFolders(): Promise<FanFolder[]> {
  const response = await http.get("/api/v1/folders");
  const normalizeFolder = (folder: Record<string, unknown>): FanFolder => ({
    id: Number(folder.id),
    name: String(folder.name ?? ""),
    slug: folder.slug as string | null | undefined,
    type: folder.type as string | null | undefined,
    externalUrl: folder.external_url as string | null | undefined,
    postsCount: folder.posts_count == null ? null : Number(folder.posts_count),
    thumbnailUrl: rewriteMediaUrl(folder.thumbnail_url as string | null | undefined),
    gridDisplay: folder.is_grid_view == null ? null : Number(folder.is_grid_view),
    courseProgress: folder.course_progress && typeof folder.course_progress === "object"
      ? {
          completedLessons: Number((folder.course_progress as Record<string, unknown>).completed_lessons ?? 0),
          totalLessons: Number((folder.course_progress as Record<string, unknown>).total_lessons ?? 0),
          score: Number((folder.course_progress as Record<string, unknown>).score ?? 0),
        }
      : null,
    isLocked: folder.is_locked as boolean | null | undefined,
    oneTimePurchase: normalizeCoursePurchaseState(folder.one_time_purchase),
    subFolders: toRecordArray(folder.subfolders).map((sub) => normalizeFolder(sub)),
  });

  return toRecordArray(response.data?.data).map((folder) => normalizeFolder(folder));
}

export async function fetchMemberships(): Promise<Membership[]> {
  const response = await http.get("/api/v1/admin/membership-packages");
  return Array.isArray(response.data?.data)
    ? response.data.data.map((item: Record<string, unknown>) => ({
        id: Number(item.id),
        name: String(item.name ?? ""),
        cashierProductId: item.cashier_product_id as string | null | undefined,
        level: item.level == null ? null : Number(item.level),
        price: item.price as string | null | undefined,
        currency: item.currency as string | null | undefined,
        badgeUrl: rewriteMediaUrl(item.badge_url as string | null | undefined),
        badgeColor: item.badge_color as string | null | undefined,
        isActive: item.is_active == null ? null : Number(item.is_active),
        isSelected: item.is_selected == null ? null : Number(item.is_selected),
        isSubscribed: item.is_subscribed as boolean | null | undefined,
      }))
    : [];
}

export async function toggleBookmark(postId: number, bookmarked: boolean) {
  if (bookmarked) {
    await http.delete(`/api/v1/bookmarks/${postId}`);
    return;
  }

  await http.post("/api/v1/bookmarks", { post_id: postId });
}

export async function likePost(postId: number, currentLikeScore = 0) {
  const vote = currentLikeScore === 1 ? 0 : 1;
  await http.post("/api/v1/likes", { model: "post_like", item_id: postId, vote });
}

export async function addComment(postId: number, comment: string, parentId?: number | null, commentableId?: number | null): Promise<Comment> {
  const response = await http.post(`/api/v1/posts/${postId}/comments`, {
    parent_comment_id: parentId ?? null,
    comment,
    commented_user_id: commentableId ?? null,
  });

  return normalizeComment(response.data?.data ?? {});
}

export async function fetchCommentReplies(commentId: number, page = 1): Promise<Comment[]> {
  const response = await http.get(`/api/v1/comments/${commentId}/replies?page=${page}`);
  return Array.isArray(response.data?.data) ? response.data.data.map((comment: Record<string, unknown>) => normalizeComment(comment)) : [];
}

export async function deletePost(postId: number) {
  await http.delete(`/api/v1/admin/posts/${postId}`);
}

export async function publishPost(postId: number, notifyAll: boolean) {
  const formData = new FormData();
  formData.set("is_notify_all", notifyAll ? "1" : "0");
  await http.post(`/api/v1/admin/posts/${postId}/publish`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

async function saveBasePost(input: SavePostInput) {
  const payload: Record<string, unknown> = {
    title: input.title,
    subtitle: input.subtitle ?? "",
    text: input.description ?? "",
    tags: Object.fromEntries(input.tags.map((tag, index) => [String(index), tag])),
    is_published: input.publish ? 1 : 0,
    is_main_feed: input.mainFeedExcluded ? 0 : 1,
    is_countdown: input.isCountdown ? 1 : 0,
    type: input.type,
  };

  if (input.id) payload.id = input.id;
  if (input.folderId) payload.folder_id = input.folderId;
  if (input.membershipId && input.type !== "course") payload.membership_id = input.membershipId;
  if (input.scheduledAt) payload.scheduled_at = input.scheduledAt;
  if (input.type === "course") {
    payload.lesson_is_intro = input.courseIsIntro ? 1 : 0;
    if (input.courseIsIntro) {
      payload.course_part_number = null;
    }
  }
  if (input.coursePartNumber && !input.courseIsIntro) payload.course_part_number = input.coursePartNumber;
  if (input.lessonFollowUp) {
    payload.lesson_follow_up = {
      reminder: input.lessonFollowUp.reminder ?? "",
      notes: input.lessonFollowUp.notes ?? "",
      questions: input.lessonFollowUp.questions.map((question) => ({
        prompt: question.prompt,
        helper_text: question.helperText ?? "",
        choices: question.choices,
        correct_choice_index: question.correctChoiceIndex ?? null,
      })),
    };
  }

  const response = await http.post("/api/v1/admin/posts", payload);
  return Number(response.data?.data?.id);
}

async function createMedia(postId: number, sortId = 1, externalUrl?: string) {
  const formData = new FormData();
  formData.set("sort_id", String(sortId));
  if (externalUrl) {
    formData.set("external_url", externalUrl);
  }

  const response = await http.post(`/api/v1/posts/${postId}/media`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return Number(response.data?.data?.id);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function uploadMediaChunk(
  mediaId: number,
  file: File,
  start: number,
  endExclusive: number,
  onUploadProgress?: (progress: number) => void,
) {
  const chunk = file.slice(start, endExclusive);
  const formData = new FormData();
  formData.set("media_id", String(mediaId));
  formData.set("file", chunk, file.name);

  const contentRange = `bytes ${start}-${endExclusive - 1}/${file.size}`;

  for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      await http.post("/api/v1/files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Content-Range": contentRange,
        },
        timeout: 0,
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (!onUploadProgress) {
            return;
          }

          const loadedInChunk = Math.max(0, Math.min(chunk.size, event.loaded ?? 0));
          const absoluteLoaded = start + loadedInChunk;
          const total = file.size || endExclusive;
          onUploadProgress(Math.max(0, Math.min(100, Math.round((absoluteLoaded / total) * 100))));
        },
      });

      return;
    } catch (error) {
      if (attempt >= MAX_CHUNK_RETRIES) {
        throw error;
      }

      await sleep(500 * attempt);
    }
  }
}

async function uploadMedia(mediaId: number, file: File, onUploadProgress?: (progress: number) => void) {
  if (file.size <= DEFAULT_MEDIA_CHUNK_SIZE_BYTES) {
    await uploadMediaChunk(mediaId, file, 0, file.size, onUploadProgress);
    return;
  }

  for (let start = 0; start < file.size; start += DEFAULT_MEDIA_CHUNK_SIZE_BYTES) {
    const endExclusive = Math.min(file.size, start + DEFAULT_MEDIA_CHUNK_SIZE_BYTES);
    await uploadMediaChunk(mediaId, file, start, endExclusive, onUploadProgress);
  }
}

export async function savePost(input: SavePostInput) {
  const postId = await saveBasePost(input);
  const files = input.files?.length ? input.files : input.file ? [input.file] : [];

  if (files.length > 0) {
    input.onUploadProgress?.(0);

    for (const [index, file] of files.entries()) {
      const mediaId = await createMedia(postId, index + 1);
      await uploadMedia(mediaId, file, (progress) => {
        if (!input.onUploadProgress) {
          return;
        }

        const overall = Math.round(((index + progress / 100) / files.length) * 100);
        input.onUploadProgress(overall);
      });
    }

    input.onUploadProgress?.(100);
  }

  if (input.externalUrl && input.type === "live") {
    await createMedia(postId, 1, input.externalUrl);
  }

  return postId;
}
